import { FC, useRef, useState } from 'react';
import { MinusSmIcon, PlusSmIcon } from '@heroicons/react/solid';

import {
  TIME_TRIGGER_OFFSET_MAX,
  TIME_TRIGGER_TIMING_OPTIONS,
} from '../Automations/automationMetadata';
import type { TimeTriggerTiming } from '../Automations/automationMetadata';
import { MAIL_SEQUENCE_FIELD_LABEL_CLASS } from './scheduleMailFieldLabel';

interface Props {
  offset: number;
  onChange: (offset: number) => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

const getTimingFromOffset = (offset: number): TimeTriggerTiming => {
  if (!Number.isFinite(offset)) return 'on_date';
  if (offset < 0) return 'before';
  if (offset > 0) return 'after';
  return 'on_date';
};

const ScheduleMailOffsetControl: FC<Props> = ({ offset, onChange }) => {
  const safeOffset = Number.isFinite(offset)
    ? clamp(offset, -TIME_TRIGGER_OFFSET_MAX, TIME_TRIGGER_OFFSET_MAX)
    : 0;
  const timing = getTimingFromOffset(safeOffset);
  const absDays = safeOffset === 0 ? 1 : Math.abs(safeOffset);

  const [draft, setDraft] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const applySign = (days: number): number =>
    timing === 'before' ? -days : days;

  const resolvedDays = (() => {
    if (draft === null) return absDays;
    const raw = parseInt(draft, 10);
    return clamp(isNaN(raw) ? absDays : raw, 1, TIME_TRIGGER_OFFSET_MAX);
  })();

  const displayValue = draft !== null ? draft : absDays;

  const handleTimingChange = (newTiming: TimeTriggerTiming) => {
    const base = resolvedDays;
    setDraft(null);
    if (newTiming === 'on_date') {
      onChange(0);
    } else if (newTiming === 'before') {
      onChange(-base);
    } else {
      onChange(base);
    }
  };

  const commitInput = () => {
    if (draft === null) return;
    const raw = parseInt(draft, 10);
    const clamped = clamp(
      isNaN(raw) ? absDays : raw,
      1,
      TIME_TRIGGER_OFFSET_MAX,
    );
    setDraft(null);
    onChange(applySign(clamped));
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="min-w-0">
        <label className={MAIL_SEQUENCE_FIELD_LABEL_CLASS}>Timing</label>
        <div className="flex space-x-1 rounded-xl bg-neutral-50 p-1">
          {TIME_TRIGGER_TIMING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`text-sm flex-1 rounded-lg px-2 py-1.5 font-medium focus:outline-none focus:ring-2 focus:ring-secondary-50 ${
                timing === option.value
                  ? 'bg-white text-green-150 shadow'
                  : 'text-neutral-125 hover:text-green-150'
              }`}
              onClick={() => handleTimingChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {timing !== 'on_date' ? (
        <div className="w-fit justify-self-start">
          <label className={MAIL_SEQUENCE_FIELD_LABEL_CLASS}>
            Days {timing === 'before' ? 'before' : 'after'}
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                onChange(applySign(resolvedDays - 1));
              }}
              disabled={resolvedDays <= 1}
              className="text-neutral-130 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-neutral-100 hover:bg-neutral-50 disabled:opacity-40 disabled:hover:bg-transparent"
              aria-label="Decrease days"
            >
              <MinusSmIcon className="h-4 w-4" />
            </button>
            <input
              ref={inputRef}
              type="number"
              min={1}
              max={TIME_TRIGGER_OFFSET_MAX}
              value={displayValue}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitInput();
              }}
              className="text-sm w-14 rounded-md border border-neutral-100 bg-white px-2 py-1.5 text-center text-body focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
            />
            <button
              type="button"
              onClick={() => {
                const next = Math.min(
                  resolvedDays + 1,
                  TIME_TRIGGER_OFFSET_MAX,
                );
                setDraft(null);
                onChange(applySign(next));
              }}
              disabled={resolvedDays >= TIME_TRIGGER_OFFSET_MAX}
              className="text-neutral-130 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-neutral-100 hover:bg-neutral-50 disabled:opacity-40 disabled:hover:bg-transparent"
              aria-label="Increase days"
            >
              <PlusSmIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <div aria-hidden className="hidden sm:block" />
      )}
    </div>
  );
};

export default ScheduleMailOffsetControl;
